import { ValidationErrorData } from '../typings/common';
import { KeywordsData, KeywordsEditorBodyView } from '../typings/keywordsEditorData';
import { PhrasesSelectorPhrase, PhrasesSelectorWizardGroupInfo } from '../typings/phrases';

export enum KeywordsEditorActionType {
    OPEN = '@KeywordsEditorAction/OPEN',
    APPLY = '@KeywordsEditorAction/APPLY',
    CHANGE = '@KeywordsEditorAction/CHANGE',
    UPDATE = '@KeywordsEditorAction/UPDATE',
    CLOSE = '@KeywordsEditorAction/CLOSE',
    SWITCH_TAB = '@KeywordsEditorAction/SWITCH_TAB',
    CHANGE_VIEW = '@KeywordsEditorAction/CHANGE_VIEW',
    FETCH_STAT = '@KeywordsEditorAction/FETCH_STAT',
    UPDATE_STAT = '@KeywordsEditorAction/UPDATE_STAT',
    DELETE_KEYWORD = '@KeywordsEditorAction/DELETE_KEYWORD',
    SET_REFINE_KEYWORD_INDEX = '@KeywordsEditorAction/SET_REFINE_KEYWORD_INDEX',
    REFINE_KEYWORD = '@KeywordsEditorAction/REFINE_KEYWORD',
    SET_ERRORS = '@KeywordsEditorAction/SET_ERRORS'
}

interface KeywordsEditorBaseAction {
    editorId: string;
}

export interface KeywordsEditorOpenAction extends KeywordsEditorBaseAction {
    type: KeywordsEditorActionType.OPEN;
    keywords: KeywordsData[];
}

export function keywordsEditorOpen(editorId: string, keywords: KeywordsData[]): KeywordsEditorOpenAction {
    return {
        type: KeywordsEditorActionType.OPEN,
        editorId,
        keywords
    };
}

export interface KeywordsEditorApplyChangesAction extends KeywordsEditorBaseAction {
    type: KeywordsEditorActionType.APPLY;
}

export function keywordsEditorApplyChanges(editorId: string): KeywordsEditorApplyChangesAction {
    return {
        type: KeywordsEditorActionType.APPLY,
        editorId
    };
}

export interface KeywordsEditorChangeAction extends KeywordsEditorBaseAction {
    type: KeywordsEditorActionType.CHANGE;
    value: string;
}

export function keywordsEditorChange(editorId: string, value: string): KeywordsEditorChangeAction {
    return {
        type: KeywordsEditorActionType.CHANGE,
        editorId,
        value
    };
}

export interface KeywordsEditorUpdateAction extends KeywordsEditorBaseAction {
    type: KeywordsEditorActionType.UPDATE;
    keywords: string[];
}

export function keywordsEditorUpdate(editorId: string, keywords: string[]): KeywordsEditorUpdateAction {
    return {
        type: KeywordsEditorActionType.UPDATE,
        editorId,
        keywords
    };
}

export interface KeywordsEditorCloseAction extends KeywordsEditorBaseAction {
    type: KeywordsEditorActionType.CLOSE;
}

export function keywordsEditorClose(editorId: string): KeywordsEditorCloseAction {
    return {
        type: KeywordsEditorActionType.CLOSE,
        editorId
    };
}

export interface KeywordsEditorSwitchTabAction extends KeywordsEditorBaseAction {
    type: KeywordsEditorActionType.SWITCH_TAB;
    tabIndex: number;
}

export function keywordsEditorSwitchTab(editorId: string, tabIndex: number): KeywordsEditorSwitchTabAction {
    return {
        type: KeywordsEditorActionType.SWITCH_TAB,
        editorId,
        tabIndex
    };
}

export interface KeywordsEditorChangeViewAction extends KeywordsEditorBaseAction {
    type: KeywordsEditorActionType.CHANGE_VIEW;
    view: KeywordsEditorBodyView;
}

export function keywordsEditorChangeView(
    editorId: string,
    view: KeywordsEditorBodyView
): KeywordsEditorChangeViewAction {
    return {
        type: KeywordsEditorActionType.CHANGE_VIEW,
        editorId,
        view
    };
}

export interface KeywordsEditorFetchStatAction extends KeywordsEditorBaseAction {
    type: KeywordsEditorActionType.FETCH_STAT;
    groupInfo: PhrasesSelectorWizardGroupInfo;
}

export function keywordsEditorFetchStat(
    editorId: string,
    groupInfo: PhrasesSelectorWizardGroupInfo
): KeywordsEditorFetchStatAction {
    return {
        type: KeywordsEditorActionType.FETCH_STAT,
        editorId,
        groupInfo
    };
}

export interface KeywordsEditorUpdateStatAction extends KeywordsEditorBaseAction {
    type: KeywordsEditorActionType.UPDATE_STAT;
    statData: number[];
}

export function keywordsEditorUpdateStat(editorId: string, statData: number[]): KeywordsEditorUpdateStatAction {
    return {
        type: KeywordsEditorActionType.UPDATE_STAT,
        editorId,
        statData
    };
}

export interface KeywordsEditorDeleteKeywordAction extends KeywordsEditorBaseAction {
    type: KeywordsEditorActionType.DELETE_KEYWORD;
    keywordIndex: number;
}

export function keywordsEditorDeleteKeyword(editorId: string, keywordIndex: number): KeywordsEditorDeleteKeywordAction {
    return {
        type: KeywordsEditorActionType.DELETE_KEYWORD,
        editorId,
        keywordIndex
    };
}

export interface KeywordsEditorSetRefineKeywordIndexAction extends KeywordsEditorBaseAction {
    type: KeywordsEditorActionType.SET_REFINE_KEYWORD_INDEX;
    keywordIndex?: number;
}

export function keywordsEditorSetRefineKeywordIndex(
    editorId: string,
    keywordIndex?: number
): KeywordsEditorSetRefineKeywordIndexAction {
    return {
        type: KeywordsEditorActionType.SET_REFINE_KEYWORD_INDEX,
        editorId,
        keywordIndex
    };
}

export interface KeywordsEditorRefineKeywordAction extends KeywordsEditorBaseAction {
    type: KeywordsEditorActionType.REFINE_KEYWORD;
    phrase: PhrasesSelectorPhrase;
}

export function keywordsEditorRefineKeyword(
    editorId: string,
    phrase: PhrasesSelectorPhrase
): KeywordsEditorRefineKeywordAction {
    return {
        type: KeywordsEditorActionType.REFINE_KEYWORD,
        editorId,
        phrase
    };
}

export interface KeywordsEditorSetErrorsAction extends KeywordsEditorBaseAction {
    type: KeywordsEditorActionType.SET_ERRORS;
    errors: ValidationErrorData[];
}

export function keywordsEditorSetErrors(
    editorId: string,
    errors: ValidationErrorData[]
): KeywordsEditorSetErrorsAction {
    return {
        type: KeywordsEditorActionType.SET_ERRORS,
        editorId,
        errors
    };
}

export type KeywordsEditorAction =
    | KeywordsEditorApplyChangesAction
    | KeywordsEditorChangeAction
    | KeywordsEditorOpenAction
    | KeywordsEditorCloseAction
    | KeywordsEditorSwitchTabAction
    | KeywordsEditorChangeViewAction
    | KeywordsEditorUpdateAction
    | KeywordsEditorFetchStatAction
    | KeywordsEditorUpdateStatAction
    | KeywordsEditorDeleteKeywordAction
    | KeywordsEditorSetRefineKeywordIndexAction
    | KeywordsEditorRefineKeywordAction
    | KeywordsEditorSetErrorsAction;
